"use strict";
/**
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const theme_1 = require("../../theme");
const tabIndicator_component_1 = require("../support/components/tabIndicator.component");
/**
 * `BottomNavigation` component is designed to be a Bottom Tab Bar.
 * Can be used for navigation.
 *
 * @extends React.Component
 *
 * @property {number} selectedIndex - Determines index of the selected tab.
 *
 * @property {string} appearance - Determines the appearance of the component.
 * Can be `default` or `noIndicator`.
 *
 * @property {ReactElement<TabProps> | ReactElement<TabProps>[]} children - Determines tabs of the Bottom Navigation.
 *
 * @property {StyleProp<ViewStyle>} indicatorStyle - Determines styles of the indicator.
 *
 * @property {(index: number) => void} onSelect - Triggered on select value.
 *
 * @property {ViewProps} ...ViewProps - Any props applied to View component.
 *
 * @overview-example BottomNavigationSimpleUsage
 *
 * @overview-example Using with React Navigation
 *
 * ```
 * import React from 'react';
 * import { SafeAreaView } from 'react-native';
 * import { NavigationContainer } from '@react-navigation/native';
 * import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
 * import { BottomNavigation, BottomNavigationTab, Layout, Text } from '../..';
 *
 * const BottomTab = createBottomTabNavigator();
 *
 * const UsersScreen = () => (
 *   <Layout style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
 *     <Text category='h1'>USERS</Text>
 *   </Layout>
 * );
 *
 * const OrdersScreen = () => (
 *   <Layout style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
 *     <Text category='h1'>ORDERS</Text>
 *   </Layout>
 * );
 *
 * const BottomTabBar = ({ navigation, state }) => {
 *
 *   const onSelect = (index) => {
 *     navigation.navigate(state.routeNames[index]);
 *   };
 *
 *   return (
 *     <SafeAreaView>
 *       <BottomNavigation selectedIndex={state.index} onSelect={onSelect}>
 *         <BottomNavigationTab title='USERS'/>
 *         <BottomNavigationTab title='ORDERS'/>
 *       </BottomNavigation>
 *     </SafeAreaView>
 *   );
 * };
 *
 * const TabNavigator = () => (
 *   <BottomTab.Navigator tabBar={props => <BottomTabBar {...props} />}>
 *     <BottomTab.Screen name='Users' component={UsersScreen}/>
 *     <BottomTab.Screen name='Orders' component={OrdersScreen}/>
 *   </BottomTab.Navigator>
 * );
 *
 * export const AppNavigator = () => (
 *   <NavigationContainer>
 *     <TabNavigator/>
 *   </NavigationContainer>
 * );
 * ```
 *
 * @example BottomNavigationWithoutIndicator
 *
 * @overview-example BottomNavigationWithIcons
 *
 * @example BottomNavigationInlineStyling
 */
class BottomNavigationComponent extends react_1.default.Component {
    constructor() {
        super(...arguments);
        this.onTabSelect = (index) => {
            if (this.props.onSelect && this.props.selectedIndex !== index) {
                this.props.onSelect(index);
            }
        };
        this.getComponentStyle = (source) => {
            const { indicatorHeight, indicatorBackgroundColor, ...containerParameters } = source;
            return {
                container: containerParameters,
                item: {},
                indicator: {
                    height: indicatorHeight,
                    backgroundColor: indicatorBackgroundColor,
                },
            };
        };
        this.renderIndicatorElement = (positions, style) => {
            const { indicatorStyle, selectedIndex } = this.props;
            return (react_1.default.createElement(tabIndicator_component_1.TabIndicator, { key: 0, style: [style, styles.indicator, indicatorStyle], selectedPosition: selectedIndex, positions: positions }));
        };
        this.renderTabElement = (element, index) => {
            return react_1.default.cloneElement(element, {
                key: index,
                style: [styles.item, element.props.style],
                selected: index === this.props.selectedIndex,
                onSelect: () => this.onTabSelect(index),
            });
        };
        this.renderTabElements = (source) => {
            return react_1.default.Children.map(source, this.renderTabElement);
        };
        this.renderComponentChildren = (style) => {
            const tabElements = this.renderTabElements(this.props.children);
            const hasIndicator = style.indicator.height > 0;
            return [
                hasIndicator && this.renderIndicatorElement(tabElements.length, style.indicator),
                ...tabElements,
            ];
        };
    }
    render() {
        const { themedStyle, style, ...derivedProps } = this.props;
        const { container, ...componentStyles } = this.getComponentStyle(themedStyle);
        const [indicatorElement, ...tabElements] = this.renderComponentChildren(componentStyles);
        return (react_1.default.createElement(react_native_1.View, Object.assign({}, derivedProps, { style: [container, styles.container, style] }),
            indicatorElement,
            tabElements));
    }
}
exports.BottomNavigationComponent = BottomNavigationComponent;
BottomNavigationComponent.styledComponentName = 'BottomNavigation';
BottomNavigationComponent.defaultProps = {
    selectedIndex: 0,
};
const styles = react_native_1.StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
    item: {
        flex: 1,
    },
    indicator: {
        position: 'absolute',
    },
});
exports.BottomNavigation = theme_1.styled(BottomNavigationComponent);
//# sourceMappingURL=bottomNavigation.component.js.map